import React, { useState, useEffect, useMemo } from 'react';
import { AiOutlineClose, AiOutlineSave } from 'react-icons/ai';
import FolderTreePicker from '../FolderTreePicker/FolderTreePicker';

/**
 * Modal de edición de folder. Permite renombrar (nombre) y mover (padre).
 *
 * El picker de "Nuevo padre" excluye el propio folder y todos sus descendientes
 * para evitar ciclos (validación pre-flight; el backend también valida).
 *
 * Props:
 * - open: bool
 * - folder: { id, name, parent } del folder a editar
 * - folders: lista plana {id, name, parent} con todos los folders del árbol
 * - onCancel: () => void
 * - onSubmit: ({ id, name, parent, nameChanged, parentChanged }) => Promise<void>
 */
const FolderEditModal = ({ open, folder, folders, onCancel, onSubmit }) => {
  const [name, setName] = useState('');
  const [parent, setParent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && folder) {
      setName(folder.name || '');
      setParent(folder.parent || '');
      setSaving(false);
    }
  }, [open, folder]);

  // Filtra del picker el propio folder + sus descendientes (no se puede mover
  // bajo sí mismo). Backend valida con isAncestorOf, pero la UI lo previene.
  const pickableFolders = useMemo(() => {
    if (!folder) return folders || [];
    const blocked = new Set([folder.id]);
    let changed = true;
    while (changed) {
      changed = false;
      (folders || []).forEach((f) => {
        if (f.parent && blocked.has(f.parent) && !blocked.has(f.id)) {
          blocked.add(f.id);
          changed = true;
        }
      });
    }
    return (folders || []).filter((f) => !blocked.has(f.id));
  }, [folder, folders]);

  if (!open || !folder) return null;

  const nameChanged = name.trim() !== (folder.name || '');
  const parentChanged = (parent || null) !== (folder.parent || null);
  const nameInvalid = !name.trim();
  const canSubmit = !nameInvalid && (nameChanged || parentChanged) && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onSubmit({
        id: folder.id,
        name: name.trim(),
        parent: parent || null,
        nameChanged,
        parentChanged,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} data-testid="folder-edit-modal">
      <form onSubmit={handleSubmit} style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Editar folder: <code style={styles.code}>{folder.id}</code></h2>
          <button type="button" onClick={onCancel} style={styles.closeBtn} aria-label="Cerrar">
            <AiOutlineClose />
          </button>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Nombre</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            autoFocus
            data-testid="folder-edit-name"
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Folder padre (vacío = raíz)</label>
          <FolderTreePicker
            folders={pickableFolders}
            value={parent}
            onChange={setParent}
            placeholder="Sin padre — folder raíz"
          />
          {parentChanged && (
            <p style={styles.warningHint}>
              Mover un folder dispara reindex en cascada async de los documentos
              en sus series descendientes (ACL efectiva se recalcula).
            </p>
          )}
        </div>

        <div style={styles.footer}>
          <button type="button" onClick={onCancel} style={styles.btnCancel}>Cancelar</button>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{ ...styles.btnPrimary, ...(canSubmit ? {} : styles.btnDisabled) }}
            data-testid="folder-edit-submit"
          >
            <AiOutlineSave style={{ marginRight: 6 }} />
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { background: '#FFFFFF', padding: '24px', borderRadius: '8px', minWidth: '440px', maxWidth: '520px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { margin: 0, fontSize: '18px', color: '#00468B', fontWeight: 'bold' },
  code: { background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px', fontSize: '14px', color: '#374151' },
  closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6B7280' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '14px', color: '#374151', fontWeight: 500 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  warningHint: { margin: '6px 0 0', fontSize: '12px', color: '#92400E', background: '#FEF3C7', padding: '6px 10px', borderRadius: '4px' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' },
  btnCancel: { padding: '8px 16px', border: '1px solid #D1D5DB', background: '#FFFFFF', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  btnPrimary: { padding: '8px 16px', background: '#00468B', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'inline-flex', alignItems: 'center' },
  btnDisabled: { background: '#9CA3AF', cursor: 'not-allowed' },
};

export default FolderEditModal;
